import { ApiError, createAction } from 'redux-api-middleware';
import { endpointWithUserId } from '../../common/url';
import { State as StoreState } from '../types/State';
import { Dispatch, ThunkAction } from '../types/Store';
import { Fetcher } from './ActivityRedemption';
import { HTTP_POST } from './ApiAction';
import { loadBonusRewards } from './BonusShop';
import { Profile, SubProfile } from './Profile';

export const PLACE_BONUS_REWARD_ORDER = 'barmer-bonus-shared/shop/bonus/orders/PLACE' as const;
export const PLACE_BONUS_REWARD_ORDER_SUCCESS = 'barmer-bonus-shared/shop/bonus/orders/SUCCESS' as const;
export const PLACE_BONUS_REWARD_ORDER_FAILURE = 'barmer-bonus-shared/shop/bonus/orders/FAILURE' as const;
export const RESET_BONUS_REWARD_ORDER = 'barmer-bonus-shared/shop/bonus/orders/RESET' as const;

type PlaceBonusRewardOrder = {
  type: typeof PLACE_BONUS_REWARD_ORDER;
  payload?: ApiError;
  error?: boolean;
};
type ResetBonusRewardOrder = { type: typeof RESET_BONUS_REWARD_ORDER };
type BonusRewardOrderSuccess = { type: typeof PLACE_BONUS_REWARD_ORDER_SUCCESS };
type BonusRewardOrderFailure = {
  type: typeof PLACE_BONUS_REWARD_ORDER_FAILURE;
  payload: ApiError;
  error: boolean;
};

export type Action =
  | PlaceBonusRewardOrder
  | ResetBonusRewardOrder
  | BonusRewardOrderSuccess
  | BonusRewardOrderFailure;

export const BONUS_ORDER_LOADING = 'loading' as const;
export const BONUS_ORDER_SUCCESS = 'success' as const;
export const BONUS_ORDER_ERROR = 'error' as const;
export const BONUS_ORDER_UNKNOWN = 'unknown' as const;

export type OrderState =
  | typeof BONUS_ORDER_LOADING
  | typeof BONUS_ORDER_SUCCESS
  | typeof BONUS_ORDER_ERROR
  | typeof BONUS_ORDER_UNKNOWN;

export type BonusRewardDeliveryAddress = {
  addressSupplement: string;
  city: string;
  countryCode: string;
  firstname: string;
  lastname: string;
  namePrefix: string;
  salutation: Salutation;
  street: string;
  title: string;
  zip: string;
};

export const SALUTATION_MALE = 'MALE' as const;
export const SALUTATION_FEMALE = 'FEMALE' as const;

export type Salutation = typeof SALUTATION_FEMALE | typeof SALUTATION_MALE;

export type BonusRewardItem = {
  iban: string; // <- note: this will be empty string for physical goods.
  receiverFirstname: string;
  receiverLastname: string;
  receiverKVNR: string;
  rewardId: number;
};

export type BonusSubventionRewardItem = {
  iban: string;
  rewardId: number;
};

export type State = {
  orderState: OrderState;
  error?: ApiError;
};

const initialState: State = {
  orderState: BONUS_ORDER_UNKNOWN,
  error: undefined,
};

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case PLACE_BONUS_REWARD_ORDER:
      return {
        ...state,
        orderState: action.payload === undefined ? BONUS_ORDER_LOADING : BONUS_ORDER_ERROR,
        error: action.payload ? action.payload : undefined,
      };

    case PLACE_BONUS_REWARD_ORDER_SUCCESS:
      return {
        ...state,
        orderState: BONUS_ORDER_SUCCESS,
      };

    case PLACE_BONUS_REWARD_ORDER_FAILURE:
      return {
        ...state,
        orderState: BONUS_ORDER_ERROR,
        error: action.payload,
      };

    case RESET_BONUS_REWARD_ORDER:
      return initialState;

    default:
      return state;
  }
}

export const placeBonusRewardOrder = (
  item: BonusRewardItem,
  userId: string,
  address?: BonusRewardDeliveryAddress,
): ThunkAction => {
  return async (dispatch: Dispatch) => {
    await dispatch(
      createAction({
        endpoint: endpointWithUserId('/shop/bonus/orders', userId),
        types: [
          PLACE_BONUS_REWARD_ORDER,
          PLACE_BONUS_REWARD_ORDER_SUCCESS,
          PLACE_BONUS_REWARD_ORDER_FAILURE,
        ],
        method: HTTP_POST,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deliveryAddress: address, item }),
      }),
    );

    dispatch(loadBonusRewards(userId));
  };
};

export const placeBonusSubventionRewardOrder = <T>(
  item: BonusSubventionRewardItem,
  documents: Array<T>,
  userId: string,
  formDataPartCreator: (document: T) => Blob,
  fetcher?: Fetcher,
): ThunkAction => {
  const data = new FormData();
  data.append('meta', JSON.stringify(item));
  documents.forEach((document: T) => data.append('document', formDataPartCreator(document)));
  return async (dispatch: Dispatch) => {
    await dispatch(
      createAction({
        endpoint: endpointWithUserId('/shop/bonus/subvention-orders', userId),
        types: [
          PLACE_BONUS_REWARD_ORDER,
          PLACE_BONUS_REWARD_ORDER_SUCCESS,
          PLACE_BONUS_REWARD_ORDER_FAILURE,
        ],
        method: HTTP_POST,
        body: data,
        fetch: fetcher,
      }),
    );

    dispatch(loadBonusRewards(userId));
  };
};

export const resetBonusOrder = (): Action => {
  return { type: RESET_BONUS_REWARD_ORDER };
};

export const getAddressFromState = (
  globalState: StoreState,
): BonusRewardDeliveryAddress | null | undefined => {
  if (globalState.profile.profile != null) {
    let profile: Profile | SubProfile;
    ({ profile } = globalState.profile);

    if (globalState.dashboard.activeUserId) {
      const additionalInsuredProfile = globalState.profile.profile.additionalInsured.find(
        (insured) => insured.id === globalState.dashboard.activeUserId,
      );

      if (additionalInsuredProfile) {
        profile = additionalInsuredProfile;
      }
    }

    return {
      addressSupplement: '',
      city: profile.city,
      countryCode: profile.countryCode,
      firstname: profile.firstname,
      lastname: profile.lastname,
      namePrefix: profile.namePrefix,
      salutation:
        profile.salutation.localeCompare('FEMALE') === 0 ? SALUTATION_FEMALE : SALUTATION_MALE,
      street: profile.street,
      title: profile.title,
      zip: profile.zip,
    };
  }

  return null;
};
